import { useState, useEffect, useCallback } from 'react';

const STATS_KEY = 'sap_b1_daily_stats';

/** Returns today's date as 'YYYY-MM-DD' in local time */
const todayStr = () => new Date().toLocaleDateString('en-CA'); // 'YYYY-MM-DD'

const freshStats = () => ({
  date: todayStr(),
  queriesToday: 0,
  latencySamples: [], // last 20 response times in ms
  avgLatencyMs: null,
});

/**
 * useStats — industry-standard stats hook
 *
 * Persists to localStorage. Daily counters auto-reset at midnight.
 * Latency is a rolling average of the last 20 real API response times.
 *
 * Usage:
 *   const { stats, recordQuery } = useStats();
 *   recordQuery(842); // call with actual ms after each API response
 */
export function useStats() {
  const [stats, setStats] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STATS_KEY));
      // If it was saved today, restore it; otherwise start fresh
      if (saved && saved.date === todayStr()) return saved;
    } catch { /* corrupt storage — ignore and start fresh */ }
    return freshStats();
  });

  // Persist every time stats change
  useEffect(() => {
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
  }, [stats]);

  /**
   * Call this after every successful query.
   * @param {number} latencyMs  - actual round-trip time in milliseconds
   */
  const recordQuery = useCallback((latencyMs) => {
    setStats(prev => {
      const today = todayStr();
      // If the day has rolled over, start fresh counts
      const base = prev.date === today ? prev : freshStats();

      const queriesToday = base.queriesToday + 1;
      const latencySamples = [...base.latencySamples, latencyMs].slice(-20);
      const avgLatencyMs = Math.round(
        latencySamples.reduce((sum, v) => sum + v, 0) / latencySamples.length
      );

      return { ...base, date: today, queriesToday, latencySamples, avgLatencyMs };
    });
  }, []);

  return { stats, recordQuery };
}
